import { Head, Link } from "@inertiajs/react";
import { ArrowLeft, FileText, Hash, Package, Tag } from "lucide-react";

import AliasManager from "@/components/alias-manager";
import InlineEditableCategorySubcategory from "@/components/inline-editable-category-subcategory";
import InlineEditableField from "@/components/inline-editable-field";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import AppLayout from "@/layouts/app-layout";
import {
  addAliasProductPath,
  productPath,
  productsPath,
  removeAliasProductPath,
} from "@/routes";
import type { BreadcrumbItem, Product } from "@/types";

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Productos",
    href: productsPath(),
  },
];

interface ProductShowProps {
  product: Product;
}

export default function ProductShow({ product }: ProductShowProps) {
  const breadcrumbsWithCurrent: BreadcrumbItem[] = [
    ...breadcrumbs,
    {
      title: product.normalized_name,
      href: "#",
    },
  ];

  return (
    <AppLayout breadcrumbs={breadcrumbsWithCurrent}>
      <Head title={`Producto: ${product.normalized_name}`} />

      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <Button variant="ghost" size="sm" asChild className="mb-3">
              <Link href={productsPath()}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Volver a Productos
              </Link>
            </Button>
            <h1 className="text-3xl font-bold">{product.normalized_name}</h1>
            <p className="text-muted-foreground">
              Gestiona la información del producto
            </p>
          </div>
        </div>

        {/* General Information Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Información General
            </CardTitle>
            <CardDescription>
              Haz clic en el ícono de lápiz para editar cualquier campo
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InlineEditableField
                label="Nombre Normalizado"
                value={product.normalized_name}
                field="normalized_name"
                updateUrl={productPath(product)}
                icon={FileText}
                placeholder="Nombre del producto"
              />
              <InlineEditableField
                label="Código Interno"
                value={product.internal_code}
                field="internal_code"
                updateUrl={productPath(product)}
                icon={FileText}
                placeholder="Código interno"
              />
            </div>

            <Separator className="my-6" />

            <InlineEditableField
              label="Descripción"
              value={product.description}
              field="description"
              type="textarea"
              updateUrl={productPath(product)}
              icon={FileText}
              placeholder="Agregar descripción..."
            />
          </CardContent>
        </Card>

        {/* Category/Subcategory */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5" />
              Categorización
            </CardTitle>
            <CardDescription>
              Edita la categoría y subcategoría del producto
            </CardDescription>
          </CardHeader>
          <CardContent>
            <InlineEditableCategorySubcategory
              categoryId={product.category_id ?? null}
              subcategoryId={product.subcategory_id ?? null}
              category={product.category}
              subcategory={product.subcategory}
              updateUrl={productPath(product)}
            />
          </CardContent>
        </Card>

        {/* Aliases Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Hash className="h-5 w-5" />
              Alias del Producto
            </CardTitle>
            <CardDescription>
              Agrega diferentes formas en que proveedores y solicitantes pueden
              nombrar este producto. La IA usa estos alias para hacer
              coincidencias automáticas precisas.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <AliasManager
              aliases={product.aliases ?? []}
              addAliasUrl={addAliasProductPath(product.id)}
              removeAliasUrl={removeAliasProductPath(product.id)}
            />
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
